import { ApiClient } from '../client'
import { 
  Target, 
  TargetDto,
  TargetPoolResponse,
  ImportPreviewResponse 
} from '../types'

export class TargetPoolApi {
  constructor(private client: ApiClient) {}

  async list(organisationId: string): Promise<TargetPoolResponse> {
    const response = await this.client.get<any>(`/v1/organisations/${organisationId}/targets`)
    return response?.data || response
  }

  async count(organisationId: string): Promise<number> {
    const response = await this.client.get<any>(`/v1/organisations/${organisationId}/targets/count`)
    return response?.data?.count ?? response?.count ?? 0
  }

  async addSingle(organisationId: string, target: Target): Promise<TargetDto> {
    const response = await this.client.post<any>(
      `/v1/organisations/${organisationId}/targets`,
      { targets: [target] }
    )
    return response?.data?.target || response.target
  }

  async bulkImport(
    organisationId: string, 
    targets: Target[]
  ): Promise<{ imported: number }> {
    const response = await this.client.post<any>(
      `/v1/organisations/${organisationId}/targets`,
      { targets }
    )
    return { imported: response?.data?.imported || response.imported }
  }

  async previewImport(
    organisationId: string, 
    targets: Target[]
  ): Promise<ImportPreviewResponse> {
    const response = await this.client.post<any>(
      `/v1/organisations/${organisationId}/targets/import/preview`,
      { targets }
    )
    return response?.data || response
  }

  async remove(organisationId: string, targetId: string): Promise<void> {
    await this.client.delete(`/v1/organisations/${organisationId}/targets/${targetId}`)
  }

  async removeBatch(organisationId: string, targetIds: string[]): Promise<void> {
    // Use request directly since delete doesn't support body in the current client
    await this.client.request(
      `/v1/organisations/${organisationId}/targets/batch`,
      { 
        method: 'DELETE',
        body: JSON.stringify(targetIds)
      }
    )
  }

  async clear(organisationId: string): Promise<void> {
    await this.client.delete(`/v1/organisations/${organisationId}/targets`)
  }

  async exportCsv(organisationId: string): Promise<void> {
    // Get token from client
    const token = this.client.getToken()
    
    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL || 'https://api.phisheez.net'}/v1/organisations/${organisationId}/targets/export`,
      {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      }
    )

    if (!response.ok) {
      throw new Error('Failed to export CSV')
    }

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition')
    console.log('[exportCsv] Content-Disposition header:', contentDisposition)
    
    let filename = `targets-${organisationId}-${Date.now()}.csv`
    
    if (contentDisposition) {
      // Try multiple patterns for filename extraction
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      console.log('[exportCsv] Filename match:', filenameMatch)
      
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/["']/g, '')
        console.log('[exportCsv] Extracted filename:', filename)
      }
    }
    
    console.log('[exportCsv] Final filename:', filename)
    
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  parseCsv(file: File): Promise<Target[]> {
    return new Promise((resolve, reject) => {
      import('papaparse').then(Papa => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (header: string) => {
            const normalized = header.trim().toLowerCase().replace(/\s+/g, '_')
            const mapping: Record<string, string> = {
              'email': 'email',
              'given_name': 'given_name',
              'first_name': 'given_name',
              'surname': 'surname',
              'last_name': 'surname',
              'job_title': 'job_title',
              'title': 'job_title',
              'department': 'department',
              'dept': 'department'
            }
            return mapping[normalized] || normalized
          },
          complete: (results) => {
            const targets: Target[] = results.data
              .filter((row: any) => row.email)
              .map((row: any) => ({
                email: row.email?.trim() || '',
                given_name: row.given_name?.trim() || undefined,
                surname: row.surname?.trim() || undefined,
                job_title: row.job_title?.trim() || undefined,
                department: row.department?.trim() || undefined
              }))
            resolve(targets)
          },
          error: (error) => {
            reject(new Error(`CSV parsing failed: ${error.message}`))
          }
        })
      }).catch(reject)
    })
  }
}
